import React, { useState } from 'react';
import { AlertTriangle, CalendarClock, Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { wpatch } from '@/components/website/api';

const DAY = 86400000;

export function renewalState(e) {
  if (e.status !== 'live') return 'na';
  if (!e.renewal_date) return 'missing';
  const d = new Date(e.renewal_date);
  if (Number.isNaN(d.getTime())) return 'set';
  const days = Math.ceil((d - new Date()) / DAY);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'soon';
  return 'set';
}

const fmt = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Inline hosting-renewal date for a website-log row. Live sites with no date get an amber prompt + date picker. */
export function RenewalCell({ e, onSaved }) {
  const state = renewalState(e);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(e.renewal_date || '');
  const [busy, setBusy] = useState(false);
  if (state === 'na') return null;

  const save = async () => {
    setBusy(true);
    try {
      const d = await wpatch(`/staff/website-log/${e.id}/renewal`, { renewal_date: val });
      toast.success(val ? `Renewal date saved for ${e.customer || e.domain}` : 'Renewal date cleared');
      setEditing(false); onSaved(e.id, d.renewal_date);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (state === 'missing' || editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`wl-renewal-edit-${e.id}`}>
        {state === 'missing' && !editing && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800" data-testid={`wl-renewal-missing-${e.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Hosting renewal date missing</span>
        )}
        <input type="date" value={val} onChange={(ev) => setVal(ev.target.value)} className="h-8 rounded-md border border-amber-300 bg-white px-2 text-sm" aria-label="Hosting renewal date" data-testid={`wl-renewal-input-${e.id}`} />
        <Button size="sm" onClick={save} disabled={busy || (!val && state === 'missing')} className="h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`wl-renewal-save-${e.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Save</Button>
        {editing && <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setVal(e.renewal_date || ''); }} className="h-8" aria-label="Cancel" data-testid={`wl-renewal-cancel-${e.id}`}><X className="w-3.5 h-3.5" /></Button>}
      </div>
    );
  }

  const tone = state === 'overdue' ? 'text-rose-700' : state === 'soon' ? 'text-amber-700' : 'text-gray-600';
  return (
    <button type="button" onClick={() => setEditing(true)} className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold ${tone} hover:underline`} data-testid={`wl-renewal-${e.id}`}>
      <CalendarClock className="w-3.5 h-3.5" /> Hosting renews {fmt(e.renewal_date)}{state === 'overdue' && ' · overdue'}{state === 'soon' && ' · due soon'}
      <Pencil className="w-3 h-3 opacity-60" />
    </button>
  );
}
